import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';

import { credentialKeys, listCredentials } from '../../entities/credential';
import {
  libraryKeys,
  listModels,
  parseSample,
  testPrompt,
  type TLibraryMessage,
} from '../../entities/library';
import { Button } from '../../shared/ui/button';
import { CodeView } from '../../shared/ui/code-view';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { PlayIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { Note } from '../../shared/ui/note';
import { RefusalNote } from '../../shared/ui/refusal-note';
import { SectionPanel } from '../../shared/ui/section-panel';
import { Select } from '../../shared/ui/select';

export interface IPromptTestSample {
  /** The event as typed: JSON text, parsed at run time so a half-typed one is not lost. */
  eventText: string;
  vars: Record<string, string>;
}

interface IPromptTestProps {
  messages: readonly TLibraryMessage[];
  variables: readonly string[];
  sample: IPromptTestSample;
  onSample: (next: IPromptTestSample) => void;
  /** Problems in the messages block a run: the server would refuse it the same way. */
  blocked: string | null;
}

const DEFAULT_MODEL = 'claude-sonnet-5';

const money = (usd: number | null): string =>
  usd === null ? 'not priced' : `$${usd.toLocaleString('en-US', { maximumFractionDigits: 6 })}`;

// The Library's "Test": the messages as they stand, rendered on this sample, one real call on a
// workspace AI account. It spends today's AI budget like a flow test does.
export const PromptTest = ({
  messages,
  variables,
  sample,
  onSample,
  blocked,
}: IPromptTestProps) => {
  const credentials = useQuery({ queryKey: credentialKeys.list, queryFn: listCredentials });
  const accounts = (credentials.data ?? []).filter((entry) => entry.connectorKey === 'ai');
  const [credentialId, setCredentialId] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const chosen = credentialId || accounts[0]?.id || '';

  useEffect(() => {
    if (!credentialId && accounts[0]) setCredentialId(accounts[0].id);
  }, [credentialId, accounts]);

  const models = useQuery({
    queryKey: libraryKeys.models(chosen),
    queryFn: () => listModels(chosen),
    enabled: chosen !== '',
    staleTime: 60_000,
    retry: false,
  });
  const modelOptions = models.data?.ok ? models.data.options : null;

  const event = parseSample(sample.eventText);
  const run = useMutation({
    mutationFn: () =>
      testPrompt({
        messages: messages.map(({ role, content }) => ({ role, content })),
        credentialId: chosen,
        model,
        sample: { event: event.value, vars: sample.vars },
      }),
  });
  const result = run.data;

  return (
    <SectionPanel title="Test" note="one real call on a workspace account">
      <div className="flex flex-col gap-3 px-3 py-3">
        {credentials.data && accounts.length === 0 && (
          <Note>
            No AI account in this workspace yet.{' '}
            <Link to="/settings/credentials" className="text-primary hover:underline">
              Add one in Credentials
            </Link>{' '}
            to test prompts.
          </Note>
        )}
        {credentials.isError && <RefusalNote error={credentials.error} />}

        {accounts.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="flex min-w-0 flex-col">
              <FieldLabel htmlFor="prompt-test-account">Account</FieldLabel>
              <Select
                id="prompt-test-account"
                value={chosen}
                onChange={setCredentialId}
                options={accounts.map((entry) => ({ value: entry.id, label: entry.name }))}
              />
            </div>
            <div className="flex min-w-0 flex-col">
              <FieldLabel htmlFor="prompt-test-model">Model</FieldLabel>
              {modelOptions ? (
                <Select
                  id="prompt-test-model"
                  value={model}
                  onChange={setModel}
                  options={modelOptions}
                />
              ) : (
                <Input
                  id="prompt-test-model"
                  size="row"
                  mono
                  value={model}
                  onChange={(next) => setModel(next.target.value)}
                />
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col">
          <FieldLabel htmlFor="prompt-test-event">Sample event</FieldLabel>
          <CodeView
            id="prompt-test-event"
            value={sample.eventText}
            language="json"
            invalid={event.error !== null}
            placeholder="Paste an event as JSON"
            onChange={(eventText) => onSample({ ...sample, eventText })}
            className="max-h-json-field-max min-h-json-field rounded-control"
          />
          <Hint className={event.error ? 'text-danger' : ''}>
            {event.error ?? '{{event.*}} reads this; the editor offers its fields as you type.'}
          </Hint>
        </div>

        {variables.map((name) => (
          <div key={name} className="flex flex-col">
            <FieldLabel htmlFor={`prompt-test-var-${name}`}>
              <span className="font-mono">vars.{name}</span>
            </FieldLabel>
            <Input
              id={`prompt-test-var-${name}`}
              size="row"
              value={sample.vars[name] ?? ''}
              placeholder="What a flow would bind it to"
              onChange={(next) =>
                onSample({ ...sample, vars: { ...sample.vars, [name]: next.target.value } })
              }
            />
          </div>
        ))}

        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            waiting={run.isPending}
            waitingLabel="Asking the model"
            disabled={!chosen || !model.trim() || event.error !== null || blocked !== null}
            onClick={() => run.mutate()}
          >
            <PlayIcon size={10} />
            Run the prompt
          </Button>
          {blocked && <span className="text-meta text-muted">{blocked}</span>}
        </div>

        {run.isError && <RefusalNote error={run.error} />}

        {result && (
          <div className="flex flex-col gap-2">
            {result.ok ? (
              <pre className="max-h-json-field-max overflow-auto rounded-control bg-surface-sunken px-3 py-3 font-mono text-data whitespace-pre-wrap text-primary">
                {result.text}
              </pre>
            ) : (
              <Note tone="danger">
                {result.error?.message ?? 'The model did not answer.'}{' '}
                <span className="font-mono text-micro">{result.error?.code}</span>
              </Note>
            )}
            <FactList>
              <FactRow label="Model" mono>
                {result.model ?? model}
              </FactRow>
              <FactRow label="Tokens in · out" mono>
                {result.tokensIn.toLocaleString('en-US')} ·{' '}
                {result.tokensOut.toLocaleString('en-US')}
              </FactRow>
              <FactRow label="Cost" mono>
                {money(result.costUsd)}
              </FactRow>
              <FactRow label="Took" mono>
                {result.durationMs.toLocaleString('en-US')} ms
              </FactRow>
            </FactList>
            <details className="text-meta text-muted">
              <summary className="cursor-pointer">What was sent</summary>
              <ol className="mt-2 flex flex-col gap-2">
                {result.rendered.map((message, index) => (
                  <li key={index} className="flex flex-col gap-1">
                    <span className="font-mono text-micro text-faint">{message.role}</span>
                    <span className="whitespace-pre-wrap text-primary">{message.content}</span>
                  </li>
                ))}
              </ol>
            </details>
          </div>
        )}
      </div>
    </SectionPanel>
  );
};
